/**
 * User Settings Hook
 * 
 * Fetches user's profile settings including orderContexts array.
 * Returns settings or empty object if not configured.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ExternalOrderConfig {
  enabled: boolean;
  shortcode: string;
  // Product - can be from existing products OR custom entry
  productId?: string;       // If selected from products table
  productName: string;      // Always populated (from selection or custom)
  productPrice: number;
  productUnit?: string;
  // Category for the order
  category?: string;
  // Business settings
  businessPhone?: string;
  deliveryNote?: string;
  minQuantity?: number;
}

export interface CourierQuickAction {
  id: string;        // UUID for unique identification
  label: string;     // e.g., "Repair pickup", "Return item"
  enabled: boolean;  // Toggle on/off
}

export interface OrderIdFormat {
  prefix: string;           // e.g., "ORD", "INV", ""
  separator: string;        // "-", "/", "", "_"
  dateFormat: 'YYMMDD' | 'DDMMYY' | 'none';
  sequenceLength: number;   // 3, 4, 5, 6 digits
}

export interface OrderIdConfig {
  mode: 'unified' | 'per-source';
  unifiedFormat: OrderIdFormat;
  perSourceFormats?: {
    manual: { prefix: string };
    external: { prefix: string };
    ai: { prefix: string };
    courier: { prefix: string };
  };
}

export const DEFAULT_ORDER_ID_CONFIG: OrderIdConfig = {
  mode: 'unified',
  unifiedFormat: {
    prefix: '',
    separator: '-',
    dateFormat: 'YYMMDD',
    sequenceLength: 4,
  }
};

// Pause configuration (when intake link is disabled)
export interface PauseConfig {
  reason?: 'busy' | 'out_of_stock' | 'closed' | 'vacation' | 'technical' | 'other';
  message?: string;  // Custom message for customers
  resumeAt?: string; // ISO date, optional
}

// Delivery fee configuration
export interface DeliveryConfig {
  enabled: boolean;
  fee?: number;           // Fixed fee in GEL (e.g., 5.00)
  freeOver?: number;      // Free delivery threshold (e.g., 50.00)
  note?: string;          // e.g., "Tbilisi only"
}

// External order intake settings namespace
export interface ExternalOrderIntakeConfig {
  pause?: PauseConfig;
  delivery?: DeliveryConfig;
}

// Bank account for multiple bank support
export interface BankAccount {
  id: string;             // UUID
  bankName: string;       // e.g., "TBC Bank", "საქართველოს ბანკი"
  iban: string;           // GE00XX...
  instructions?: string;  // Optional per-bank note
  isDefault?: boolean;    // Primary display
}

export interface PaymentConfig {
  enableBankTransfer: boolean;
  enableCash?: boolean;           // Default: true (backward compatible)
  bankAccounts?: BankAccount[];   // NEW: array (up to 4)
  defaultMethod: 'cash' | 'bank_transfer';
  // Legacy fields (keep for backward compatibility)
  bankName?: string;
  iban?: string;
  paymentInstructions?: string;
}

export interface ContactLink {
  type: 'phone' | 'messenger' | 'instagram' | 'facebook';
  value: string;
}

export interface BrandingConfig {
  logoUrl?: string;
  brandColor?: string;      // hex, e.g. "#e63946"
  tagline?: string;         // max 80 chars
  introText?: string;       // max 300 chars
  contactLinks?: ContactLink[];  // max 4
}

export interface UserSettings {
  orderContexts?: string[];
  externalOrderConfig?: ExternalOrderConfig;
  courierQuickActions?: CourierQuickAction[];
  orderIdConfig?: OrderIdConfig;
  enableDashboardPulse?: boolean;
  enablePulseSound?: boolean;
  enableCourierQuickActions?: boolean;
  paymentConfig?: PaymentConfig;
  externalOrderIntake?: ExternalOrderIntakeConfig;
  branding?: BrandingConfig;
}

export interface UserSettingsResult extends UserSettings {
  companyName?: string;
}

export function useUserSettings() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-settings", user?.id],
    queryFn: async (): Promise<UserSettingsResult> => {
      if (!user?.id) return {};

      const { data, error } = await supabase
        .from("profiles")
        .select("settings, company_name")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Failed to fetch user settings:", error);
        return {};
      }

      // Parse settings JSONB - it may be null or an object
      const settings = data?.settings as UserSettings | null;
      return {
        ...(settings || {}),
        companyName: data?.company_name || undefined,
      };
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
